// P&L chart — wraps the copied Canvas `createProjectionChart` via a ref +
// useEffect. Rebuilds on data change and cleans the canvas on unmount. No
// charting dependency; the chart is hand-rolled Canvas 2D in the vendored lib.

import { useEffect, useMemo, useRef } from 'react';
import type { PortalCostItem, PortalSavingsItem } from '../../api/types';
import { calculateProductPL } from '../../lib/financial/product-pl';
import { getCumulativeValues } from '../../lib/financial/aggregation';
import { getMonthLabels } from '../../lib/financial/projections';
import { createProjectionChart } from '../../lib/financial/projection-chart';
import { toCostBucket, toSavingsBucket } from './helpers';

interface PlChartProps {
  costs: PortalCostItem[];
  savings: PortalSavingsItem[];
  height?: number;
}

const HORIZON_MONTHS = 36;

export function PlChart({ costs, savings, height = 200 }: PlChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const series = useMemo(() => {
    const pl = calculateProductPL(
      toCostBucket(costs),
      toSavingsBucket(savings),
      { horizonMonths: HORIZON_MONTHS },
    );
    const cumulative = getCumulativeValues(pl.netContribution.monthly);
    const labels = getMonthLabels(cumulative.length);
    return { values: cumulative, labels };
  }, [costs, savings]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const canvas = createProjectionChart({
      values: series.values,
      labels: series.labels,
      color: '#3B72B8',
      height,
    });
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', 'Cumulative net contribution over 36 months');
    container.appendChild(canvas);
    return () => {
      // Clean the canvas on unmount / before the next rebuild.
      if (canvas.parentNode === container) container.removeChild(canvas);
    };
  }, [series, height]);

  return <div ref={containerRef} className="w-full overflow-hidden rounded-[10px] border border-line bg-white" />;
}
